import { Switch, Route, Redirect } from "wouter";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { queryClient } from "./lib/queryClient";
import { isPostHogEnabled, usePostHogPageview } from "./lib/posthog";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AgencyProvider } from "@/lib/agencyContext";
import { useAuth } from "@/hooks/use-auth";
import Dashboard from "@/pages/Dashboard";
import Policies from "@/pages/Policies";
import PolicyDetailRedirect from "@/pages/PolicyDetailRedirect";
import PolicyEdit from "@/pages/PolicyEdit";
import Insureds from "@/pages/Insureds";
import InsuredDetail from "@/pages/InsuredDetail";
import InsuredDetailNew from "@/pages/InsuredDetailNew";
import InsuredEdit from "@/pages/InsuredEdit";
import ReviewQueue from "@/pages/ReviewQueue";
import Companies from "@/pages/Companies";
import InsuranceCompanyDetail from "@/pages/InsuranceCompanyDetail";
import Settings from "@/pages/Settings";
import SystemSettings from "@/pages/SystemSettings";
import ActivityLogs from "@/pages/ActivityLogs";
import Leads from "@/pages/Leads";
import Login from "@/pages/Login";
import NotFound from "@/pages/not-found";

function AccessDenied() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-8">
      <div className="max-w-md text-center space-y-4">
        <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold font-heading text-foreground" data-testid="text-access-denied">Access Denied</h1>
        <p className="text-muted-foreground">
          Only <strong>@logrock.com</strong> email addresses are allowed to access this application. 
          Please sign in with your LogRock account.
        </p>
        <a
          href="/api/logout"
          className="inline-flex items-center justify-center px-6 py-2 bg-[#111827] text-white rounded-md font-medium hover:bg-[#1a2332] transition-colors"
          data-testid="button-try-again"
        >
          Sign in with a different account
        </a>
      </div>
    </div>
  );
}

function AuthenticatedRouter() {
  const { user, isLoading, isAuthenticated, error } = useAuth();
  usePostHogPageview();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 border-4 border-[#F3C14A] border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (error?.message?.includes("restricted")) {
    return <AccessDenied />;
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <AgencyProvider>
      <Switch>
        <Route path="/"><Redirect to="/dashboard" /></Route>
        <Route path="/dashboard" component={Dashboard} />
        <Route path="/policies" component={Policies} />
        <Route path="/policies/:id/edit" component={PolicyEdit} />
        <Route path="/policies/:id" component={PolicyDetailRedirect} />
        <Route path="/insureds" component={Insureds} />
        <Route path="/insureds/:id/edit" component={InsuredEdit} />
        <Route path="/insureds/:id" component={InsuredDetailNew} />
        <Route path="/insureds-old/:id" component={InsuredDetail} />
        <Route path="/review-queue" component={ReviewQueue} />
        <Route path="/companies" component={Companies} />
        <Route path="/companies/insurers/:id" component={InsuranceCompanyDetail} />
        <Route path="/leads">{user?.crmAccess ? <Leads /> : <NotFound />}</Route>
        <Route path="/activity-log" component={ActivityLogs} />
        <Route path="/settings" component={Settings} />
        <Route path="/system-settings" component={SystemSettings} />
        <Route component={NotFound} />
      </Switch>
    </AgencyProvider>
  );
}

function AppInner() {
  return (
    <TooltipProvider>
      <Toaster />
      <AuthenticatedRouter />
    </TooltipProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {isPostHogEnabled() ? (
        <PostHogProvider client={posthog}>
          <AppInner />
        </PostHogProvider>
      ) : (
        <AppInner />
      )}
    </QueryClientProvider>
  );
}

export default App;
